import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TimeField } from '@ui/components/ui/time-field';
import { calendarDay } from '@app/utils/helpers/date-utils';
import { WorkdayDateField } from './WorkdayDateField';

// A moment, as the day it happened and the hour it happened at — the calendar this app draws, and the two boxes it
// types times into.

/** The two halves of a `datetime-local` value, or nothing for a half that is unanswered. */
const split = (value: string): { day: Date | null; time: string } => {
  const [date, time] = value.split('T');
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');

  return {
    // A calendar day is held as UTC midnight, the way the rest of the app holds one.
    day: parts ? new Date(Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))) : null,
    time: time ?? '',
  };
};

const join = (day: Date | null, time: string): string => {
  // A day with no hour yet is `yyyy-MM-ddT`, which every reader of this value rejects. Returning nothing
  // would drop the day somebody picked while leaving it on screen, and save a moment they never gave.
  if (!day) return '';
  const pad = (part: number): string => String(part).padStart(2, '0');

  return `${day.getUTCFullYear()}-${pad(day.getUTCMonth() + 1)}-${pad(day.getUTCDate())}T${time}`;
};

const DateTimeField = ({
  id,
  label,
  value,
  onChange,
  defaultDay,
  invalid = false,
  errorMessage,
  disabledDays,
  required = false,
  align = 'start',
}: {
  id: string;
  /** Names the day, which is the field the id points at. The hour beside it is named "At". */
  label: string;
  /** `yyyy-MM-ddTHH:mm`, or empty while either half is unanswered. */
  value: string;
  onChange: (next: string) => void;
  // The day to stand in the box while nothing has been answered, for a field whose day is nearly always the same one.
  defaultDay?: Date;
  /** Colours the field without saying anything, for a caller printing the reason itself. */
  invalid?: boolean;
  errorMessage?: string;
  disabledDays?: { before?: Date; after?: Date };
  required?: boolean;
  align?: 'start' | 'end';
}): ReactElement => {
  const { t } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Held here rather than read off `value` each render, because a moment is answered one half at a time.
  const [day, setDay] = useState<Date | null>(() => split(value).day ?? (defaultDay ? calendarDay(defaultDay) : null));
  const [time, setTime] = useState(() => split(value).time);

  // What the halves last agreed on, so a value set from outside — the form opening on a record — reaches
  // them, while a half-answered moment of their own does not wipe what is being typed.
  const emitted = useRef(value);
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    const next = split(value);
    setDay(next.day ?? (defaultDay ? calendarDay(defaultDay) : null));
    setTime(next.time);
  }, [value]);

  const emit = (nextDay: Date | null, nextTime: string): void => {
    const whole = join(nextDay, nextTime);
    emitted.current = whole;
    onChange(whole);
  };

  return (
    <div className="flex flex-wrap items-start gap-3">
      <WorkdayDateField
        required={required}
        align={align}
        className="w-36 shrink-0"
        id={id}
        // Nothing until a day is picked: today sitting in an unanswered box reads as an answer somebody gave.
        value={day}
        onChange={next => {
          setDay(next);
          emit(next, time);
        }}
        open={calendarOpen}
        onOpenChange={setCalendarOpen}
        label={label}
        placeholder={t('phillReport.pickDay')}
        disabledDays={disabledDays}
        invalid={invalid}
        errorMessage={errorMessage}
      />
      <TimeField
        required={required}
        className="shrink-0"
        id={`${id}-time`}
        label={t('reportIncident.atTime')}
        value={time}
        onChange={next => {
          setTime(next);
          emit(day, next);
        }}
        invalidMessage={t('reportIncident.badTime')}
      />
    </div>
  );
};

export default DateTimeField;
